/*
 * The application shell: persistent sidebar on desktop, compact header plus
 * fixed bottom navigation on mobile.
 *
 * The two are separate markup rather than a shrinking sidebar, because they
 * are different interactions - a paddock thumb on a phone wants five fat
 * targets pinned to the bottom of the screen, not a hamburger and a drawer.
 *
 * The Garage group is expandable and driven by the categories the API returns,
 * so adding axles or sprockets later needs no change here.
 */

import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Boxes, CalendarDays, ChevronDown, ChevronRight, CloudSun, Cog,
  FileBarChart, Gauge, LayoutDashboard, LogOut, Menu, MoreHorizontal, Timer, X,
} from 'lucide-react'
import clsx from 'clsx'
import type { User } from '../lib/api'
import { api, canSee } from '../lib/api'
import { initials } from '../lib/format'

interface Props {
  user: User
  garageCategories: { key: string; plural: string }[]
  children: React.ReactNode
}

type NavItem = {
  to: string; label: string; icon: any; end?: boolean
  isNew?: boolean; section?: string; ownerOnly?: boolean
}

const MAIN: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/sessions', label: 'Sessions', icon: Timer },
  { to: '/events', label: 'Calendar', icon: CalendarDays, section: 'calendar' },
  { to: '/tracks', label: 'Tracks', icon: Gauge, section: 'tracks' },
]

const SECONDARY: NavItem[] = [
  { to: '/weather', label: 'Tune', icon: CloudSun, isNew: true, section: 'weather' },
  { to: '/compare', label: 'Compare', icon: FileBarChart, section: 'compare' },
  { to: '/settings', label: 'Settings', icon: Cog, ownerOnly: true },
]

// A nav item shows unless its section is hidden for this person, or it is owner
// only and they are not the owner. The server enforces the same rules - this
// just keeps the menu honest.
function visible(user: User, it: NavItem): boolean {
  if (it.ownerOnly && !user.is_owner) return false
  if (it.section && !canSee(user, it.section)) return false
  return true
}

export function AppShell({ user, garageCategories, children }: Props) {
  const [garageOpen, setGarageOpen] = useState(true)
  const [moreOpen, setMoreOpen] = useState(false)
  const location = useLocation()
  const inGarage = location.pathname.startsWith('/garage')

  const signOut = async () => {
    try { await api.post('/api/auth/logout', {}) } catch { /* clear anyway */ }
    // Hard navigation so all app state and cached queries are dropped.
    window.location.href = '/'
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <Boxes size={20} color="#3aa0ff" />
          <div>GRAYSON <span>KART</span></div>
        </div>

        {MAIN.filter((it) => visible(user, it)).map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end}
            className={({ isActive }) => clsx('nav-item', isActive && 'active')}>
            <it.icon size={17} /> {it.label}
          </NavLink>
        ))}

        {canSee(user, 'garage') && (
        <button className={clsx('nav-item nav-group-toggle', inGarage && !garageOpen && 'active')}
          onClick={() => setGarageOpen((o) => !o)}>
          <span className="row"><Boxes size={17} /> Garage</span>
          {garageOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        )}
        {canSee(user, 'garage') && garageOpen && garageCategories.map((c) => (
          <NavLink key={c.key} to={`/garage/${c.key}`}
            className={({ isActive }) => clsx('nav-item sub', isActive && 'active')}>
            {c.plural}
          </NavLink>
        ))}

        {SECONDARY.filter((it) => visible(user, it)).map((it) => (
          <NavLink key={it.to} to={it.to}
            className={({ isActive }) => clsx('nav-item', isActive && 'active')}>
            <it.icon size={17} /> {it.label}
            {it.isNew && <span className="new-tag">New</span>}
          </NavLink>
        ))}

        <div className="sidebar-foot">
          <div className="avatar">
            {user.picture ? <img src={user.picture} alt="" /> : initials(user.name)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{user.name}</div>
            <div className="tiny dim">
              {user.role === 'owner' ? 'Owner'
                : user.role === 'viewer' ? 'Viewer' : 'Mechanic'}
            </div>
          </div>
          <button className="btn ghost small" title="Sign out"
            aria-label="Sign out" onClick={signOut}>
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      <div className="main">
        <div className="mobile-header">
          <div className="brand" style={{ padding: 0, fontSize: 15 }}>
            <Boxes size={18} color="#3aa0ff" />
            <div>GRAYSON <span>KART</span></div>
          </div>
          <button className="btn ghost small" onClick={() => setMoreOpen((o) => !o)}>
            {moreOpen ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>

        {moreOpen && (
          <div className="card" style={{ margin: '10px 13px 0' }}>
            {[...SECONDARY.filter((it) => visible(user, it)),
              ...(canSee(user, 'garage')
                ? [{ to: '/garage/rims', label: 'Garage', icon: Boxes } as NavItem] : [])]
              .map((it) => (
                <NavLink key={it.to} to={it.to} className="nav-item"
                  onClick={() => setMoreOpen(false)}>
                  <it.icon size={17} /> {it.label}
                </NavLink>
              ))}
            <button className="nav-item" style={{ width: '100%', textAlign: 'left' }}
              onClick={signOut}>
              <LogOut size={17} /> Sign out
            </button>
          </div>
        )}

        {children}

        <nav className="bottom-nav">
          {MAIN.filter((it) => visible(user, it)).map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) => clsx(isActive && 'active')}>
              <it.icon size={19} /> {it.label}
            </NavLink>
          ))}
          <button onClick={() => setMoreOpen((o) => !o)}>
            <MoreHorizontal size={19} /> More
          </button>
        </nav>
      </div>
    </div>
  )
}
